import { Loader2 } from "lucide-react";
import { lazy, useLayoutEffect, useState } from "react";
import { Route, Routes } from "react-router";
import Footer from "src/components/ui/Footer";
import Header from "src/components/ui/Header";
import { TestHeader } from "src/components/ui/TestHeader";
import RoutesPath from "src/constants/RoutesPath";
import useNavigateCheckout from "src/hooks/useNavigateCheckout";
import DirectDebitAuthorization from "src/pages/DirectDebitAuthorization";
import ExpiredLinkPage from "src/pages/ExpiredLinkPage";
import PreAuthorizationByGatewayPage from "src/pages/PreAuthorizationByGatewayPage";
import PreAuthorizationWaitingPage from "src/pages/PreAuthorizationWaitingPage";
import { useCheckoutDataContext } from "src/providers/SharedDataProvider";

const Order = lazy(() => import("src/pages/Order"));
const CreditCard = lazy(() => import("src/pages/CreditCard"));
const PaymentSuccess = lazy(() => import("src/pages/PaymentSuccess"));
const PaymentError = lazy(() => import("src/pages/PaymentError"));
const PixPage = lazy(() => import("src/pages/PixPage"));
const PixInstallmentsSelection = lazy(
  () => import("src/pages/PixInstallmentsSelection"),
);
const BankSlipPage = lazy(() => import("src/pages/BankSlipPage"));
const DirectDebitPage = lazy(() => import("src/pages/DirectDebitPage"));
const BankSlipInstallmentsSelection = lazy(
  () => import("src/pages/BankSlipInstallmentsSelection"),
);

const AppLayout: React.FC = () => {
  const data = useCheckoutDataContext();
  useNavigateCheckout(data);

  const [isReady, setIsReady] = useState(false);

  useLayoutEffect(() => {
    setIsReady(!!data);
  }, [data]);

  return (
    <main className="flex min-h-screen flex-col">
      <TestHeader />
      <article className="mx-auto flex w-full max-w-280 grow flex-col">
        <Header />
        {isReady ? (
          <Routes>
            <Route path="" element={<Order />} />
            <Route path={RoutesPath.PAYMENT_METHODS} element={<Order />} />

            <Route path={RoutesPath.CREDIT_CARD} element={<CreditCard />} />
            <Route
              path={RoutesPath.CREDIT_CARD_SUCCESS}
              element={<PaymentSuccess />}
            />
            <Route
              path={RoutesPath.CREDIT_CARD_SCHEDULED}
              element={<PaymentSuccess />}
            />
            <Route
              path={RoutesPath.CREDIT_CARD_ERROR}
              element={<PaymentError />}
            />

            <Route path={RoutesPath.PIX} element={<PixPage />} />
            <Route
              path={RoutesPath.PIX_INSTALLMENTS_SELECTION}
              element={<PixInstallmentsSelection />}
            />
            <Route path={RoutesPath.PIX_SUCCESS} element={<PaymentSuccess />} />
            <Route path={RoutesPath.PIX_ERROR} element={<PaymentError />} />

            <Route path={RoutesPath.BANK_SLIP} element={<BankSlipPage />} />
            <Route
              path={RoutesPath.BANK_SLIP_INSTALLMENTS_SELECTION}
              element={<BankSlipInstallmentsSelection />}
            />
            <Route
              path={RoutesPath.BANK_SLIP_SUCCESS}
              element={<PaymentSuccess />}
            />
            <Route
              path={RoutesPath.BANK_SLIP_ERROR}
              element={<PaymentError />}
            />

            <Route path={RoutesPath.BOLEPIX} element={<BankSlipPage />} />
            <Route
              path={RoutesPath.BOLEPIX_SUCCESS}
              element={<PaymentSuccess />}
            />
            <Route path={RoutesPath.BOLEPIX_ERROR} element={<PaymentError />} />
            <Route
              path={RoutesPath.PRE_AUTHORIZATION_ERROR}
              element={<PaymentError />}
            />

            <Route
              path={RoutesPath.DIRECT_DEBIT}
              element={<DirectDebitPage />}
            />
            <Route
              path={RoutesPath.DIRECT_DEBIT_AUTHORIZATION}
              element={<DirectDebitAuthorization />}
            />
            <Route
              path={RoutesPath.DIRECT_DEBIT_SUCCESS}
              element={<PaymentSuccess />}
            />
            <Route
              path={RoutesPath.DIRECT_DEBIT_ERROR}
              element={<PaymentError />}
            />

            <Route
              path={RoutesPath.PAYMENT_CONFIRMED}
              element={<PaymentSuccess />}
            />
            <Route
              path={RoutesPath.PRE_AUTHORIZATION_WAITING}
              element={<PreAuthorizationWaitingPage />}
            />
            <Route
              path={RoutesPath.PRE_AUTHORIZED_BY_GATEWAY}
              element={<PreAuthorizationByGatewayPage />}
            />
            <Route
              path={RoutesPath.EXPIRED_PAYMENT}
              element={<ExpiredLinkPage />}
            />
          </Routes>
        ) : (
          <div className="flex grow items-center justify-center py-20">
            <Loader2 className="text-primary-1 h-8 w-8 animate-spin" />
          </div>
        )}

        <Footer />
      </article>
    </main>
  );
};

export default AppLayout;
